/**
 * useConversationMessages Hook
 *
 * Real-time listener for messages in a conversation thread.
 * Handles sending messages, marking as read, and pagination.
 *
 * @module features/messaging/hooks/useConversationMessages
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  query,
  orderBy,
  limit as firestoreLimit,
  onSnapshot,
  startAfter,
  getDocs
} from 'firebase/firestore';
import { db } from '@/firebase';
import { useAuth } from '@/hooks';
import ConversationService from '@/services/messaging/ConversationService';

const PAGE_SIZE = 50;

/**
 * @param {string|null} conversationId
 * @param {Object} options
 * @param {number} options.limit - Initial messages to load
 * @param {boolean} options.autoMarkAsRead - Auto-mark conversation as read
 * @returns {Object}
 */
export default function useConversationMessages(conversationId, {
  limit = PAGE_SIZE,
  autoMarkAsRead = true
} = {}) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sending, setSending] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const oldestDocRef = useRef(null);

  // Real-time listener for messages
  useEffect(() => {
    if (!conversationId || !user?.uid) {
      setMessages([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const q = query(
      collection(db, 'conversations', conversationId, 'messages'),
      orderBy('sentAt', 'desc'),
      firestoreLimit(limit)
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const fetched = [];

        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          // Skip messages deleted for current user
          if (data.deletedFor?.includes(user.uid)) return;
          fetched.push({ id: docSnap.id, ...data, _doc: docSnap });
        });

        // Store oldest doc for pagination
        if (fetched.length > 0) {
          oldestDocRef.current = fetched[fetched.length - 1]._doc;
        }

        // Check if there might be more messages
        setHasMore(fetched.length >= limit);

        // Reverse to show oldest first (chat order), strip _doc
        setMessages(fetched.reverse().map(({ _doc, ...msg }) => msg));
        setLoading(false);
      },
      (err) => {
        console.error('Error in messages listener:', err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [conversationId, user?.uid, limit]);

  // Auto-mark as read when viewing conversation
  useEffect(() => {
    if (!conversationId || !user?.uid || !autoMarkAsRead || loading) return;

    ConversationService.markAsRead(conversationId, user.uid).catch((err) => {
      console.warn('Error marking as read:', err);
    });
  }, [conversationId, user?.uid, autoMarkAsRead, loading, messages.length]);

  /**
   * Load older messages (pagination)
   */
  const loadMore = useCallback(async () => {
    if (!conversationId || !oldestDocRef.current || !hasMore) return;

    try {
      const q = query(
        collection(db, 'conversations', conversationId, 'messages'),
        orderBy('sentAt', 'desc'),
        startAfter(oldestDocRef.current),
        firestoreLimit(PAGE_SIZE)
      );

      const snapshot = await getDocs(q);
      const older = [];

      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.deletedFor?.includes(user?.uid)) return;
        older.push({ id: docSnap.id, ...data, _doc: docSnap });
      });

      if (older.length > 0) {
        oldestDocRef.current = older[older.length - 1]._doc;
      }

      setHasMore(older.length >= PAGE_SIZE);

      // Prepend older messages (reversed to chronological)
      const olderCleaned = older.reverse().map(({ _doc, ...msg }) => msg);
      setMessages(prev => [...olderCleaned, ...prev]);
    } catch (err) {
      console.error('Error loading older messages:', err);
    }
  }, [conversationId, hasMore, user?.uid]);

  /**
   * Send a text message
   */
  const sendMessage = useCallback(async (text, replyTo = null) => {
    if (!conversationId || !user?.uid || !text.trim()) return;

    setSending(true);
    try {
      await ConversationService.sendMessage(conversationId, {
        senderId: user.uid,
        senderName: user.displayName || user.email || 'Unknown',
        senderRole: 'teacher', // TODO: get from SchoolContext
        text: text.trim(),
        replyTo
      });
    } catch (err) {
      console.error('Error sending message:', err);
      throw err;
    } finally {
      setSending(false);
    }
  }, [conversationId, user?.uid, user?.displayName, user?.email]);

  /**
   * Edit a message
   */
  const editMessage = useCallback(async (messageId, newText) => {
    if (!conversationId) return;
    await ConversationService.editMessage(conversationId, messageId, newText);
  }, [conversationId]);

  /**
   * Delete message for me
   */
  const deleteForMe = useCallback(async (messageId) => {
    if (!conversationId || !user?.uid) return;
    await ConversationService.deleteMessageForMe(conversationId, messageId, user.uid);
  }, [conversationId, user?.uid]);

  /**
   * Delete message for everyone
   */
  const deleteForEveryone = useCallback(async (messageId) => {
    if (!conversationId) return;
    await ConversationService.deleteMessageForEveryone(conversationId, messageId);
  }, [conversationId]);

  return {
    messages,
    loading,
    error,
    sending,
    hasMore,
    sendMessage,
    editMessage,
    deleteForMe,
    deleteForEveryone,
    loadMore
  };
}
